import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Package, Truck, ArrowSquareOut } from "@phosphor-icons/react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const mxn = (n) => `$${Number(n || 0).toLocaleString("es-MX")} MXN`;

const STATUS_STYLES = {
  Pagado: "bg-black text-white",
  pagado: "bg-[#25D366] text-white",
  pendiente: "bg-[#FFC439] text-black",
  rechazado: "bg-[#DC2626] text-white",
  sin_pagar: "bg-black/15 text-black/60",
  Preparando: "bg-[#009EE3] text-white",
  Enviado: "bg-[#FF5508] text-white",
  "Por llegar": "bg-[#8B5CF6] text-white",
  Entregado: "bg-[#25D366] text-white",
};

export const Rastreo = () => {
  const [orderId, setOrderId] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Rastrear mi pedido - Ruta 360";
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    setError("");
    try {
      const { data } = await axios.post(`${API}/track`, { order_id: orderId.trim(), email: email.trim() });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || "No encontramos ese pedido. Revisa tu número de pedido y tu correo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="rastreo-page" className="bg-white min-h-screen">
      <Header />
      <main className="pt-[100px]">
        <div className="max-w-2xl mx-auto px-6 py-14 lg:py-20">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <h1 className="font-display text-5xl sm:text-6xl uppercase font-semibold leading-none tracking-tight text-[#111418] mb-4">
              Rastrear mi pedido
            </h1>
            <p className="text-black/55 text-sm mb-10">
              Escribe tu número de pedido (lo encuentras en tu correo de confirmación) y el correo con el que compraste.
            </p>

            <form data-testid="rastreo-form" onSubmit={submit} className="flex flex-col gap-4 mb-10">
              <input
                data-testid="rastreo-order-input"
                required
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="Número de pedido (ej. R360-ABC123)"
                className="w-full bg-white border border-black/15 rounded-full px-5 py-3.5 text-sm outline-none focus:border-[#FF5508] transition-colors"
              />
              <input
                data-testid="rastreo-email-input"
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Correo electrónico"
                className="w-full bg-white border border-black/15 rounded-full px-5 py-3.5 text-sm outline-none focus:border-[#FF5508] transition-colors"
              />
              <button
                data-testid="rastreo-submit-button"
                type="submit"
                disabled={loading}
                className="h-12 rounded-full bg-black text-white text-sm font-semibold transition-colors duration-300 hover:bg-[#FF5508] disabled:opacity-50"
              >
                {loading ? "Buscando…" : "Rastrear pedido"}
              </button>
            </form>

            {error && (
              <p data-testid="rastreo-error" className="text-sm text-[#DC2626] mb-6">{error}</p>
            )}

            {result && (
              <div data-testid="rastreo-result" className="border border-black/10 rounded-2xl p-6 sm:p-8">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                  <p className="font-bold text-lg text-[#111418] flex items-center gap-2">
                    <Package size={20} weight="fill" className="text-[#FF5508]" /> {result.id}
                  </p>
                  <span
                    data-testid="rastreo-status"
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full ${STATUS_STYLES[result.status] || "bg-black text-white"}`}
                  >
                    {result.status}
                  </span>
                </div>
                <ul className="text-sm text-black/70 flex flex-col gap-1.5 mb-5">
                  {(result.items || []).map((it, i) => (
                    <li key={i}>{it.qty} × {it.name} | {mxn(it.price * it.qty)}</li>
                  ))}
                </ul>
                <p className="font-bold text-base mb-5">Total: {mxn(result.total)}</p>
                {result.paqueteria && (
                  <p className="text-sm text-black/70 flex items-center gap-2 mb-1">
                    <Truck size={16} weight="fill" className="text-[#FF5508]" /> Paquetería: <strong>{result.paqueteria}</strong>
                  </p>
                )}
                {result.guia && (
                  <p className="text-sm text-black/70 mb-4">Número de guía: <strong>{result.guia}</strong></p>
                )}
                {result.rastreo_url && (
                  <a
                    data-testid="rastreo-track-link"
                    href={result.rastreo_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#FF5508] text-white text-sm font-semibold transition-colors duration-300 hover:bg-black"
                  >
                    <ArrowSquareOut size={16} />
                    Rastrear mi pedido
                  </a>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};
